import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import userAPI from '@global-apis/user';
import authAPI from '@global-apis/auth';
import generalAPI, { GeneralData } from '@global-apis/general';
import { queryKeys, staleTimes } from '@root/constants/queryKeys';
import User, { UserGroup } from '@global-interfaces/User';
import { Profile } from '@global-interfaces/User';

export const useMe = (options = {}) =>
  useQuery<User, Error>({
    queryKey: queryKeys.me,
    queryFn: () => userAPI.me().then((res) => res.data),
    staleTime: staleTimes.identity,
    ...options
  });

export const useGroups = (options = {}) =>
  useQuery<UserGroup[], Error>({
    queryKey: queryKeys.groups,
    queryFn: () => authAPI.group.list().then((res) => res.data.results),
    staleTime: staleTimes.identity,
    ...options
  });

export const useGeneralSettings = (options = {}) =>
  useQuery<GeneralData, Error>({
    queryKey: queryKeys.generalSettings,
    queryFn: () => generalAPI.fetch().then((res) => res.data[0]),
    staleTime: staleTimes.identity,
    ...options
  });

export const useUpdateCurrentUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<User> }) => userAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.me });
      queryClient.invalidateQueries({ queryKey: queryKeys.usersActive });
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
    }
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Profile> }) => userAPI.updateProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.me });
      queryClient.invalidateQueries({ queryKey: queryKeys.usersActive });
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
    }
  });
};
